import { produce } from 'immer';
import { atom, selector } from 'recoil';

import {
  CarFilter,
  FeatureFilter,
  FeatureFilters,
  SpecCountType,
} from '@/types/filters/filters.types';
import { guardRecoilDefaultValue } from '@/utils/atom';

export const featuresState = atom<FeatureFilters>({
  key: 'features',
  default: {
    comfort: [],
    safetyAndTech: [],
    other: [],
  },
});

export const featuresCountState = atom<SpecCountType[] | undefined>({
  key: 'featuresCount',
  default: [],
});

export const featuresSearchOptionsSelector = selector<CarFilter[]>({
  key: 'featuresSearchOptionsSelector',
  get: ({ get }) => {
    const features = get(featuresState);

    return Object.values(features)
      .flat<FeatureFilter[]>()
      .filter((feature) => !feature.isSelected);
  },
});

export const toggleFeaturesSelector = selector<number | null>({
  key: 'toggleFeatures',
  get: () => null,
  set: ({ set, get }, featureId) => {
    const features = get(featuresState);

    const updatedFeatures = produce(features, (featuresDraft) => {
      const flatDraftFeatures = Object.values(featuresDraft).flat();

      if (featureId) {
        const feature = flatDraftFeatures.find(
          (feature) => feature.id === featureId,
        );

        feature!.isSelected = !feature?.isSelected;
        return;
      }
    });

    set(featuresState, updatedFeatures);
  },
});

export const selectedFeaturesSelector = selector<FeatureFilter[]>({
  key: 'selectedFeatures',
  get: ({ get }) => {
    const features = get(featuresState);

    return Object.values(features)
      .flat<FeatureFilter[]>()
      .filter((feature) => feature.isSelected);
  },
});

export const visibleFeaturesiltersSelector = selector<CarFilter[]>({
  key: 'visibleFeaturesiltersSelector',
  get: ({ get }) => {
    const { other } = get(featuresState);
    const featuresCount = get(featuresCountState);

    return other.map((feature) => ({
      ...feature,
      isDisabled: !featuresCount?.find(
        (group) => group?.specId === feature?.id,
      ),
    }));
  },
});

export const hasSelectedFeaturesFilterSelector = selector<boolean>({
  key: 'hasSelectedFeaturesFilter',
  get({ get }) {
    return Boolean(get(selectedFeaturesSelector).length);
  },
});

export const resetSelectedFeaturesSelector = selector<number | null>({
  key: 'resetSelectedFeatures',
  get: () => null,
  set: ({ set, get }, featureId) => {
    const features = get(featuresState);

    const updatedFeatures = produce(features, (featuresDraft) => {
      const flatDraftFeatures = Object.values(featuresDraft).flat();

      if (featureId) {
        const feature = flatDraftFeatures.find(
          (feature) => feature.id === featureId,
        );

        feature!.isSelected = false;
        return;
      }

      flatDraftFeatures.forEach((feature) => {
        if (featureId && feature.id === featureId) {
          feature.isSelected = false;
          return;
        }
        feature.isSelected = false;
      });
    });

    set(featuresState, updatedFeatures);
  },
});

export const selectFeaturesSelector = selector({
  key: 'selectFeatures',
  get: () => [],
  set: ({ set, get }, featuresIds) => {
    const features = get(featuresState);

    const updatedFeatures = produce(features, (draftFeatures) => {
      if (guardRecoilDefaultValue(featuresIds)) return;
      if (featuresIds.length) {
        featuresIds
          .map((featureId) => Number(featureId))
          .forEach((featureId) => {
            Object.values(draftFeatures).forEach((key) => {
              const feature = key.find((feature) => feature.id === featureId);

              if (feature) feature.isSelected = true;
            });
          });

        return;
      }
    });

    set(featuresState, updatedFeatures);
  },
});
